import React, { useState, useEffect, useRef } from "react";
import { StyleSheet, View, TouchableOpacity, Platform, Modal, ActivityIndicator, Image } from "react-native";
import { ThemedText } from "../ThemedText";
import { useThemeColor } from "../../hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import { File } from "expo-file-system";
import {
  useAudioRecorder,
  RecordingPresets,
  requestRecordingPermissionsAsync,
  setAudioModeAsync,
  useAudioPlayer,
  useAudioPlayerStatus,
  PermissionStatus,
} from "expo-audio";
import {
  ICapturedGeoLocation,
  getHighAccuracyLocation,
  getHighestResolutionConstraints,
  getHighFidelityAudioConstraints,
  blobToFile,
  generateCaptureFileName,
} from "../../services/media-capture";

/**
 * Props for the MediaCaptureModal component.
 */
export interface IMediaCaptureModalProps {
  mode: "photo" | "video" | "audio" | null;
  onClose: () => void;
  onCaptureComplete: (file: File | any, location: ICapturedGeoLocation | null) => void;
  initialFacingMode?: "environment" | "user";
}

/**
 * Dedicated preview player for native audio captures.
 */
function NativeAudioPreview({ uri, tintColor }: { uri: string; tintColor: string }) {
  const player = useAudioPlayer(uri);
  const status = useAudioPlayerStatus(player);

  const togglePlay = () => {
    if (status.playing) {
      player.pause();
    } else {
      player.play();
    }
  };

  return (
    <View style={styles.nativeAudioPlayerContainer}>
      <TouchableOpacity
        onPress={togglePlay}
        style={[styles.nativeAudioPlayBtn, { backgroundColor: tintColor }]}
        accessibilityLabel={status.playing ? "Pause preview" : "Play preview"}
      >
        <Ionicons name={status.playing ? "pause" : "play"} size={22} color="#FFFFFF" />
      </TouchableOpacity>
      <View style={{ marginLeft: 12 }}>
        <ThemedText style={{ fontSize: 13, fontWeight: "600" }}>
          {status.playing ? "Playing..." : "Tap to Play"}
        </ThemedText>
        <ThemedText style={{ fontSize: 11, opacity: 0.7 }}>
          {Math.floor(status.currentTime || 0)}s / {Math.floor(status.duration || 0)}s
        </ThemedText>
      </View>
    </View>
  );
}

/**
 * Fullscreen capture modal supporting high-resolution photography, 4K/HD video recording,
 * and high-fidelity audio capture with automatic high-accuracy GPS tagging.
 */
export function MediaCaptureModal({
  mode,
  onClose,
  onCaptureComplete,
  initialFacingMode = "environment",
}: IMediaCaptureModalProps) {
  if (!mode) return null;

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  // Camera facing mode state (rear: "environment", front: "user")
  const [facingMode, setFacingMode] = useState<"environment" | "user">(initialFacingMode);

  useEffect(() => {
    if (initialFacingMode) {
      setFacingMode(initialFacingMode);
    }
  }, [initialFacingMode]);

  // Geolocation state
  const [gpsLocation, setGpsLocation] = useState<ICapturedGeoLocation | null>(null);
  const [acquiringGps, setAcquiringGps] = useState(true);

  // Stream & Recording state
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  // Preview state
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturedContentType, setCapturedContentType] = useState<string>("");
  const [nativeAudioUri, setNativeAudioUri] = useState<string | null>(null);
  const [nativeAudioSize, setNativeAudioSize] = useState<number>(0);

  const audioRecorder = useAudioRecorder(RecordingPresets.HIGH_QUALITY);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<any>(null);

  // Fetch GPS coordinates immediately upon opening
  useEffect(() => {
    let isMounted = true;
    setAcquiringGps(true);
    getHighAccuracyLocation().then((loc) => {
      if (isMounted) {
        setGpsLocation(loc);
        setAcquiringGps(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [mode]);

  // Launches native camera on Android / iOS
  const launchNativeCamera = async () => {
    if (Platform.OS === "web") return;
    if (mode === "audio") return;

    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== ImagePicker.PermissionStatus.GRANTED && (status as any) !== "granted") {
        setErrorMsg("Camera permission is required to capture media.");
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: mode === "video" ? ["videos"] : ["images"],
        videoQuality: ImagePicker.UIImagePickerControllerQualityType.High,
        quality: 1.0,
        cameraType: facingMode === "user" ? ImagePicker.CameraType.front : ImagePicker.CameraType.back,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        onClose();
        return;
      }

      const asset = result.assets[0];
      const isVid = mode === "video";
      const defaultMime = isVid ? "video/mp4" : "image/jpeg";
      const mime = asset.mimeType || defaultMime;
      const fileName = asset.fileName || generateCaptureFileName(mode, mime);
      const fileItem = {
        name: fileName,
        type: mime,
        size: asset.fileSize || 0,
        uri: asset.uri,
      };

      onCaptureComplete(fileItem, gpsLocation);
      onClose();
    } catch (err: any) {
      console.error("[MediaCaptureModal] Native camera error:", err);
      setErrorMsg(err.message || "Failed to open camera on device.");
    }
  };

  // Initializes native audio subsystem & requests permissions on Android / iOS
  const initNativeAudio = async () => {
    try {
      const { status } = await requestRecordingPermissionsAsync();
      if (status !== PermissionStatus.GRANTED && (status as any) !== "granted") {
        setErrorMsg("Microphone permission is required to record audio.");
        return;
      }
      await setAudioModeAsync({
        allowsRecording: true,
        playsInSilentMode: true,
      });
    } catch (err: any) {
      console.error("[MediaCaptureModal] initNativeAudio error:", err);
      setErrorMsg(err.message || "Failed to initialize microphone.");
    }
  };

  // Initialize camera or microphone stream
  useEffect(() => {
    let currentStream: MediaStream | null = null;
    setErrorMsg(null);
    setCapturedBlob(null);
    setPreviewUrl(null);
    setNativeAudioUri(null);
    setNativeAudioSize(0);

    const initStream = async () => {
      if (Platform.OS !== "web") {
        if (mode === "audio") {
          await initNativeAudio();
        } else {
          await launchNativeCamera();
        }
        return;
      }

      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        setErrorMsg("Direct device recording requires browser media device support. Use file upload fallback.");
        return;
      }

      try {
        if (mode === "photo" || mode === "video") {
          currentStream = await navigator.mediaDevices.getUserMedia(getHighestResolutionConstraints(mode, facingMode));
        } else if (mode === "audio") {
          currentStream = await navigator.mediaDevices.getUserMedia(getHighFidelityAudioConstraints());
        }

        setStream(currentStream);

        if (videoRef.current && currentStream && (mode === "photo" || mode === "video")) {
          videoRef.current.srcObject = currentStream;
          videoRef.current.play().catch(() => {});
        }
      } catch (err: any) {
        console.error("[MediaCaptureModal] getUserMedia error:", err);
        setErrorMsg(err.message || "Failed to access camera or microphone. Please check system permissions.");
      }
    };

    initStream();

    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [mode]);

  // Clean up object URL when unmounting or resetting
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Stop camera tracks helper
  const stopAllTracks = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  // Photo snapshot handler
  const handleTakePhoto = () => {
    if (!videoRef.current || !stream) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1920;
    canvas.height = video.videoHeight || 1080;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        setCapturedBlob(blob);
        setCapturedContentType("image/jpeg");
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        stopAllTracks();
      },
      "image/jpeg",
      1.0
    );
  };

  // Video recording handler
  const handleStartVideoRecording = () => {
    if (!stream) return;
    recordedChunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : MediaRecorder.isTypeSupported("video/mp4")
      ? "video/mp4"
      : "video/webm";

    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        recordedChunksRef.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: mimeType });
      setCapturedBlob(blob);
      setCapturedContentType(mimeType);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      stopAllTracks();
    };

    recorder.start(500);
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
    setRecordingSeconds(0);

    timerIntervalRef.current = setInterval(() => {
      setRecordingSeconds((prev) => prev + 1);
    }, 1000);
  };

  const handleStopVideoRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
  };

  // Audio recording handler
  const handleStartAudioRecording = async () => {
    if (Platform.OS !== "web") {
      try {
        setErrorMsg(null);
        await audioRecorder.prepareToRecordAsync();
        audioRecorder.record();
        setIsRecording(true);
        setRecordingSeconds(0);
        timerIntervalRef.current = setInterval(() => {
          setRecordingSeconds((prev) => prev + 1);
        }, 1000);
      } catch (err: any) {
        console.error("[MediaCaptureModal] Start native audio recording error:", err);
        setErrorMsg(err.message || "Failed to start audio recording.");
      }
      return;
    }

    if (!stream) return;
    recordedChunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/mp4")
      ? "audio/mp4"
      : "audio/webm";

    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        recordedChunksRef.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: mimeType });
      setCapturedBlob(blob);
      setCapturedContentType(mimeType);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      stopAllTracks();
    };

    recorder.start(500);
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
    setRecordingSeconds(0);

    timerIntervalRef.current = setInterval(() => {
      setRecordingSeconds((prev) => prev + 1);
    }, 1000);
  };

  const handleStopAudioRecording = async () => {
    if (Platform.OS !== "web") {
      try {
        if (isRecording) {
          await audioRecorder.stop();
          setIsRecording(false);
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
          const uri = audioRecorder.uri;
          if (uri) {
            setNativeAudioUri(uri);
            setCapturedContentType("audio/m4a");
            setPreviewUrl(uri);
            try {
              const file = new File(uri);
              if (file.exists && typeof file.size === "number") {
                setNativeAudioSize(file.size);
              }
            } catch (fsErr) {
              console.warn("[MediaCaptureModal] Could not get audio file size:", fsErr);
            }
          }
        }
      } catch (err: any) {
        console.error("[MediaCaptureModal] Stop native audio recording error:", err);
        setErrorMsg(err.message || "Failed to stop audio recording.");
      }
      return;
    }

    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
  };

  // Retake handler
  const handleRetake = () => {
    if (previewUrl && Platform.OS === "web") {
      URL.revokeObjectURL(previewUrl);
    }
    setCapturedBlob(null);
    setPreviewUrl(null);
    setNativeAudioUri(null);
    setNativeAudioSize(0);
    setRecordingSeconds(0);

    if (Platform.OS !== "web") {
      return;
    }

    // Re-trigger stream initialization by resetting mode
    const currentMode = mode;
    (async () => {
      try {
        let newStream: MediaStream | null = null;
        if (currentMode === "photo" || currentMode === "video") {
          newStream = await navigator.mediaDevices.getUserMedia(getHighestResolutionConstraints(currentMode, facingMode));
        } else if (currentMode === "audio") {
          newStream = await navigator.mediaDevices.getUserMedia(getHighFidelityAudioConstraints());
        }
        setStream(newStream);
        if (videoRef.current && newStream && (currentMode === "photo" || currentMode === "video")) {
          videoRef.current.srcObject = newStream;
          videoRef.current.play().catch(() => {});
        }
      } catch (e: any) {
        setErrorMsg(e.message || "Failed to restart stream.");
      }
    })();
  };

  // Flips camera between rear ("environment") and front/selfie ("user")
  const handleFlipCamera = async () => {
    if (mode !== "photo" && mode !== "video") return;
    const nextFacingMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(nextFacingMode);

    if (Platform.OS === "web") {
      try {
        const newStream = await navigator.mediaDevices.getUserMedia(
          getHighestResolutionConstraints(mode, nextFacingMode)
        );
        const newVideoTrack = newStream.getVideoTracks()[0];

        if (stream) {
          const oldVideoTrack = stream.getVideoTracks()[0];
          if (oldVideoTrack) {
            oldVideoTrack.stop();
            stream.removeTrack(oldVideoTrack);
          }
          if (newVideoTrack) {
            stream.addTrack(newVideoTrack);
          }
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(() => {});
          }
        } else {
          setStream(newStream);
          if (videoRef.current) {
            videoRef.current.srcObject = newStream;
            videoRef.current.play().catch(() => {});
          }
        }
      } catch (err: any) {
        console.error("[MediaCaptureModal] Error flipping camera:", err);
        setErrorMsg("Failed to switch camera: " + (err.message || "Device error"));
      }
    }
  };

  // Confirm and upload
  const handleAccept = () => {
    if (Platform.OS !== "web") {
      if (!nativeAudioUri) return;
      const fileName = generateCaptureFileName("audio", "audio/m4a");
      const fileItem = {
        name: fileName,
        type: "audio/m4a",
        size: nativeAudioSize || 0,
        uri: nativeAudioUri,
      };
      onCaptureComplete(fileItem, gpsLocation);
      onClose();
      return;
    }

    if (!capturedBlob) return;
    const fileName = generateCaptureFileName(mode, capturedContentType);
    const file = blobToFile(capturedBlob, fileName, capturedContentType);
    onCaptureComplete(file, gpsLocation);
    onClose();
  };

  const formatTimer = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
  };

  return (
    <Modal visible={true} transparent={true} animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={[styles.captureContainer, { backgroundColor: cardBg, borderColor }]}>
          {/* Header */}
          <View style={[styles.headerRow, { borderBottomColor: borderColor }]}>
            <View style={styles.titleWithIcon}>
              <Ionicons
                name={mode === "photo" ? "camera" : mode === "video" ? "videocam" : "mic"}
                size={18}
                color={tintColor}
              />
              <ThemedText type="defaultSemiBold" style={styles.headerTitle}>
                {mode === "photo" ? "Take High-Res Photo" : mode === "video" ? "Record 4K/HD Video" : "Record High-Fidelity Audio"}
              </ThemedText>
            </View>

            <View style={styles.headerRightControls}>
              {(mode === "photo" || mode === "video") && !previewUrl && (
                <TouchableOpacity
                  onPress={handleFlipCamera}
                  style={[styles.flipHeaderBtn, { backgroundColor: badgeBg, borderColor }]}
                  accessibilityLabel="Turn camera around"
                  accessibilityRole="button"
                >
                  <Ionicons name="camera-reverse-outline" size={14} color={tintColor} />
                  <ThemedText style={[styles.flipBtnText, { color: tintColor }]}>
                    {facingMode === "user" ? "Front (Selfie)" : "Rear Camera"}
                  </ThemedText>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                onPress={onClose}
                style={[styles.closeBtn, { backgroundColor: badgeBg }]}
                accessibilityLabel="Close capture modal"
              >
                <ThemedText style={styles.closeBtnText}>✕ Close</ThemedText>
              </TouchableOpacity>
            </View>
          </View>

          {/* GPS Status Indicator */}
          <View style={[styles.gpsBar, { backgroundColor: badgeBg }]}>
            <Ionicons name="location-sharp" size={13} color={gpsLocation ? "#10B981" : tintColor} />
            <ThemedText style={styles.gpsText}>
              {gpsLocation
                ? `GPS Tag: ${gpsLocation.label} (High Accuracy)`
                : acquiringGps
                ? "Acquiring high-accuracy GPS coordinates..."
                : "Location tag unavailable"}
            </ThemedText>
            {acquiringGps && <ActivityIndicator size="small" color={tintColor} style={{ marginLeft: 4 }} />}
          </View>

          {/* Main Viewfinder / Review area */}
          <View style={styles.viewportArea}>
            {errorMsg ? (
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle-outline" size={36} color="#EF4444" />
                <ThemedText style={styles.errorText}>{errorMsg}</ThemedText>
                {Platform.OS !== "web" && (mode === "photo" || mode === "video") && (
                  <TouchableOpacity
                    style={[styles.actionBtn, { backgroundColor: tintColor, marginTop: 14 }]}
                    onPress={launchNativeCamera}
                  >
                    <ThemedText style={[styles.btnText, { color: "#FFFFFF" }]}>
                      {mode === "video" ? "Open Camera to Record Video" : "Open Camera to Take Photo"}
                    </ThemedText>
                  </TouchableOpacity>
                )}
              </View>
            ) : previewUrl ? (
              // Review Captured Media
              <View style={styles.previewContainer}>
                {mode === "photo" ? (
                  Platform.OS === "web" ? (
                    React.createElement("img", {
                      src: previewUrl,
                      style: { width: "100%", height: "100%", objectFit: "contain", borderRadius: 0 },
                      alt: "Captured Preview",
                    })
                  ) : (
                    <Image
                      source={{ uri: previewUrl }}
                      style={{ width: "100%", height: "100%", resizeMode: "contain" }}
                    />
                  )
                ) : mode === "video" ? (
                  Platform.OS === "web" ? (
                    React.createElement("video", {
                      src: previewUrl,
                      controls: true,
                      autoPlay: true,
                      style: { width: "100%", height: "100%", objectFit: "contain", borderRadius: 0 },
                    })
                  ) : (
                    <View style={{ alignItems: "center", justifyContent: "center", padding: 20 }}>
                      <Ionicons name="videocam" size={64} color="#EA4335" style={{ marginBottom: 12 }} />
                      <ThemedText style={{ color: "#fff", fontWeight: "600" }}>Video Recording</ThemedText>
                    </View>
                  )
                ) : (
                  <View style={styles.audioPreviewBox}>
                    <Ionicons name="musical-notes" size={48} color={tintColor} style={{ marginBottom: 12 }} />
                    <ThemedText type="defaultSemiBold" style={{ marginBottom: 8 }}>
                      Audio Clip Recorded ({formatTimer(recordingSeconds)})
                    </ThemedText>
                    {Platform.OS === "web" ? (
                      React.createElement("audio", {
                        src: previewUrl,
                        controls: true,
                        style: { width: "100%", maxWidth: 360, outline: "none" },
                      })
                    ) : (
                      <NativeAudioPreview uri={previewUrl} tintColor={tintColor} />
                    )}
                  </View>
                )}
              </View>
            ) : (
              // Active Viewfinder
              <View style={styles.liveStreamContainer}>
                {(mode === "photo" || mode === "video") && (
                  Platform.OS === "web" ? (
                    React.createElement("video", {
                      ref: (el: HTMLVideoElement | null) => {
                        videoRef.current = el;
                        if (el && stream && !el.srcObject) {
                          el.srcObject = stream;
                          el.play().catch(() => {});
                        }
                      },
                      autoPlay: true,
                      playsInline: true,
                      muted: true,
                      style: { width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#000" },
                    })
                  ) : (
                    <View style={{ flex: 1, alignItems: "center", justifyContent: "center", padding: 20 }}>
                      <Ionicons name="videocam-outline" size={64} color={tintColor} style={{ marginBottom: 16 }} />
                      <ThemedText style={{ textAlign: "center", color: "#fff" }}>
                        Camera stream is available in web browser.
                      </ThemedText>
                    </View>
                  )
                )}

                {(mode === "photo" || mode === "video") && (
                  <TouchableOpacity
                    onPress={handleFlipCamera}
                    style={styles.floatingFlipBtn}
                    accessibilityLabel="Turn camera around"
                    accessibilityRole="button"
                  >
                    <Ionicons name="camera-reverse" size={18} color="#FFFFFF" />
                    <ThemedText style={styles.floatingFlipText}>
                      {facingMode === "user" ? "Flip to Rear" : "Flip to Front"}
                    </ThemedText>
                  </TouchableOpacity>
                )}

                {mode === "audio" && (
                  <View style={styles.audioLiveBox}>
                    <Ionicons
                      name={isRecording ? "mic" : "mic-outline"}
                      size={64}
                      color={isRecording ? "#EF4444" : tintColor}
                      style={{ marginBottom: 16 }}
                    />
                    <ThemedText type="defaultSemiBold" style={styles.audioLiveTitle}>
                      {isRecording ? "Recording Audio Stream..." : "Microphone Ready"}
                    </ThemedText>
                    <ThemedText style={styles.audioLiveTimer}>
                      {formatTimer(recordingSeconds)}
                    </ThemedText>
                  </View>
                )}

                {isRecording && (
                  <View style={styles.recordingOverlay}>
                    <View style={styles.recordingDot} />
                    <ThemedText style={styles.recordingTimerText}>
                      REC {formatTimer(recordingSeconds)}
                    </ThemedText>
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Action Footer */}
          <View style={[styles.footerRow, { borderTopColor: borderColor }]}>
            {previewUrl ? (
              <View style={styles.reviewActions}>
                <TouchableOpacity
                  onPress={handleRetake}
                  style={[styles.actionBtn, styles.retakeBtn, { backgroundColor: badgeBg }]}
                >
                  <Ionicons name="refresh-outline" size={16} color={tintColor} />
                  <ThemedText style={[styles.btnText, { color: tintColor }]}>Retake</ThemedText>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleAccept}
                  style={[styles.actionBtn, styles.acceptBtn, { backgroundColor: tintColor }]}
                >
                  <Ionicons name="checkmark-outline" size={16} color="#FFFFFF" />
                  <ThemedText style={[styles.btnText, { color: "#FFFFFF" }]}>Accept & Upload</ThemedText>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.liveActions}>
                {(mode === "photo" || mode === "video") && (
                  <TouchableOpacity
                    onPress={handleFlipCamera}
                    style={[styles.actionBtn, styles.flipFooterBtn, { backgroundColor: badgeBg, borderColor }]}
                    accessibilityLabel="Turn camera around"
                    accessibilityRole="button"
                  >
                    <Ionicons name="camera-reverse-outline" size={18} color={tintColor} />
                    <ThemedText style={[styles.btnText, { color: tintColor }]}>
                      {facingMode === "user" ? "Switch to Rear" : "Switch to Front"}
                    </ThemedText>
                  </TouchableOpacity>
                )}
                {mode === "photo" && (
                  <TouchableOpacity
                    onPress={handleTakePhoto}
                    disabled={!stream}
                    style={[styles.captureShutterBtn, { backgroundColor: tintColor }]}
                    accessibilityLabel="Capture photo"
                  >
                    <Ionicons name="camera" size={24} color="#FFFFFF" />
                    <ThemedText style={styles.shutterBtnText}>Take Photo</ThemedText>
                  </TouchableOpacity>
                )}

                {mode === "video" && (
                  !isRecording ? (
                    <TouchableOpacity
                      onPress={handleStartVideoRecording}
                      disabled={!stream}
                      style={[styles.captureShutterBtn, styles.recordVideoBtn]}
                      accessibilityLabel="Start video recording"
                    >
                      <Ionicons name="radio-button-on" size={24} color="#FFFFFF" />
                      <ThemedText style={styles.shutterBtnText}>Start Recording</ThemedText>
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      onPress={handleStopVideoRecording}
                      style={[styles.captureShutterBtn, styles.stopRecordBtn]}
                      accessibilityLabel="Stop video recording"
                    >
                      <Ionicons name="stop" size={24} color="#FFFFFF" />
                      <ThemedText style={styles.shutterBtnText}>Stop Recording</ThemedText>
                    </TouchableOpacity>
                  )
                )}

                {mode === "audio" && (
                  !isRecording ? (
                    <TouchableOpacity
                      onPress={handleStartAudioRecording}
                      disabled={Platform.OS === "web" ? !stream : false}
                      style={[styles.captureShutterBtn, styles.recordAudioBtn]}
                      accessibilityLabel="Start audio recording"
                    >
                      <Ionicons name="mic" size={24} color="#FFFFFF" />
                      <ThemedText style={styles.shutterBtnText}>Start Recording</ThemedText>
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      onPress={handleStopAudioRecording}
                      style={[styles.captureShutterBtn, styles.stopRecordBtn]}
                      accessibilityLabel="Stop audio recording"
                    >
                      <Ionicons name="stop" size={24} color="#FFFFFF" />
                      <ThemedText style={styles.shutterBtnText}>Stop Recording</ThemedText>
                    </TouchableOpacity>
                  )
                )}
              </View>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  captureContainer: {
    width: "100%",
    maxWidth: 640,
    height: 520,
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    display: "flex",
    flexDirection: "column",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  titleWithIcon: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerTitle: {
    fontSize: 13,
  },
  headerRightControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  flipHeaderBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 0,
    borderWidth: 1,
  },
  flipBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
  floatingFlipBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.25)",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    zIndex: 10,
  },
  floatingFlipText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "600",
  },
  flipFooterBtn: {
    borderWidth: 1,
    marginRight: 8,
  },
  closeBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 0,
  },
  closeBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
  gpsBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 5,
    gap: 6,
  },
  gpsText: {
    fontSize: 10,
    fontWeight: "500",
    opacity: 0.85,
  },
  viewportArea: {
    flex: 1,
    backgroundColor: "#000",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
  },
  liveStreamContainer: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  previewContainer: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  audioLiveBox: {
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  audioLiveTitle: {
    fontSize: 15,
    color: "#FFFFFF",
    marginBottom: 6,
  },
  audioLiveTimer: {
    fontSize: 28,
    fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
    fontWeight: "700",
    color: "#FFFFFF",
    opacity: 0.9,
  },
  audioPreviewBox: {
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    width: "100%",
  },
  recordingOverlay: {
    position: "absolute",
    top: 12,
    left: 12,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 0,
    gap: 6,
  },
  recordingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#EF4444",
  },
  recordingTimerText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "700",
    fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
  },
  errorContainer: {
    padding: 24,
    alignItems: "center",
    gap: 12,
  },
  errorText: {
    color: "#EF4444",
    textAlign: "center",
    fontSize: 12,
    lineHeight: 18,
  },
  footerRow: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  liveActions: {
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
  },
  reviewActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    width: "100%",
  },
  actionBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 0,
  },
  retakeBtn: {
    borderWidth: 1,
    borderColor: "rgba(150, 150, 150, 0.3)",
  },
  acceptBtn: {},
  btnText: {
    fontSize: 12,
    fontWeight: "600",
  },
  captureShutterBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 0,
  },
  recordVideoBtn: {
    backgroundColor: "#DC2626",
  },
  recordAudioBtn: {
    backgroundColor: "#2563EB",
  },
  stopRecordBtn: {
    backgroundColor: "#B91C1C",
  },
  shutterBtnText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  nativeAudioPlayerContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderWidth: 1,
    borderColor: "rgba(150, 150, 150, 0.2)",
    borderRadius: 0,
    marginTop: 8,
  },
  nativeAudioPlayBtn: {
    width: 44,
    height: 44,
    borderRadius: 0,
    justifyContent: "center",
    alignItems: "center",
  },
});
